import { Element, useNode } from "@craftjs/core";
import { rem } from "polished";
import * as React from "react";
import { Actions } from "src/types";
import { ThemeContext } from "styled-components";

import TopLevelFloatingOptions from "@/components/editor/block-outline/floating-options/top-level-floating-component";
import type { ImagePosition } from "@/components/editor/image-positioner/image-positioner-component";
import Button from "@/components/editor/primitives/button/button-component";
import RichText from "@/components/editor/primitives/rich-text";
import Text from "@/components/editor/primitives/text/text-component";
import {
  BlockBackgroundColorWrapper,
  BlockMaxWidthWrapper,
} from "@/components/editor/shared/block-wrapper";
import {
  aquaLogo,
  blackLogo,
  whiteLogo,
} from "@/components/editor/shared/header/assets";
import formatLogoSize from "@/components/editor/shared/header/format-logo-size";
import HeaderSettings, {
  ImageFit,
} from "@/components/editor/shared/header/header-settings-component";
import {
  Circle,
  QuarterCircle,
  Semicircle,
} from "@/components/editor/themes/hazel/shared/vector-graphics";
import {
  ColorLabels,
  useInvertedColor,
} from "@/components/editor/utils/use-color";
import useEvent from "@/hooks/use-event";
import useRichTextCompatible from "@/hooks/use-rich-text-compatible";
import humanizeDateRange from "@/utils/dates/humanizeDateRange";

import {
  DEFAULT_BUTTON_TEXT,
  DEFAULT_HEADING_TEXT,
  DEFAULT_SUBHEADING_TEXT,
} from "./constants";
import {
  ButtonWrapper,
  Content,
  CTATextWrapper,
  Image,
  ImageWrapper,
  Logo,
  LogoWrapper,
  Wrapper,
} from "./header-option-1-component-styles";

type HeaderOption1Props = {
  backgroundImage?: string;
  backgroundImageAlt?: string;
  backgroundPosition?: ImagePosition;
  isLogoShowing?: boolean;
  logo?: string;
  logoSize?: number;
  imageFit?: ImageFit;
};

const CustomHeaderSettings = () => {
  return (
    <HeaderSettings
      optimalImageSize="630x780px"
      backgroundImageSize={{
        width: "120",
        height: "288",
      }}
    />
  );
};

export default function HeaderOption1(props: HeaderOption1Props) {
  const {
    connectors: { connect },
    id: nodeId,
  } = useNode();

  const { event } = useEvent();
  const theme = React.useContext(ThemeContext);
  const backgroundColor = theme.colors[ColorLabels.BACKGROUND];
  const showLightLogo = useInvertedColor(backgroundColor) === "#ffffff";

  const defaultLogoImage =
    backgroundColor === "#024348"
      ? aquaLogo
      : showLightLogo
      ? whiteLogo
      : blackLogo;

  const isRichTextCompatible = useRichTextCompatible(nodeId, [
    "heading",
    "cta-text",
    "subheading",
  ]);

  return (
    <BlockBackgroundColorWrapper ref={connect} id={nodeId}>
      <BlockMaxWidthWrapper maxWidth={rem(1440)}>
        <Wrapper>
          <Content>
            {props.isLogoShowing && (
              <LogoWrapper>
                <Logo
                  draggable="false"
                  src={props.logo || defaultLogoImage}
                  alt="Header Logo"
                  data-testid="header-option-1-logo"
                  style={{
                    width: formatLogoSize(props.logoSize),
                    height: "auto",
                  }}
                  isLogoResized={!isNaN(props.logoSize)}
                />
              </LogoWrapper>
            )}
            {isRichTextCompatible ? (
              <>
                <Element
                  id="heading"
                  is={RichText}
                  text={`<h2>${event.name || DEFAULT_HEADING_TEXT}</h2>`}
                />
                <CTATextWrapper>
                  <Element
                    id="cta-text"
                    is={RichText}
                    text={`<h5>${humanizeDateRange(
                      event.time_start,
                      event.time_end,
                      event.standard_timezone
                    ).join(" - ")}</h5>`}
                  />
                </CTATextWrapper>
                <Element
                  id="subheading"
                  is={RichText}
                  text={`<p>${DEFAULT_SUBHEADING_TEXT}</p>`}
                />
              </>
            ) : (
              <>
                <Element
                  id="heading"
                  is={Text}
                  element="h1"
                  text={event.name || DEFAULT_HEADING_TEXT}
                  pattern="headingOne"
                />
                <Element
                  id="cta-text"
                  is={Text}
                  element="h4"
                  text={humanizeDateRange(
                    event.time_start,
                    event.time_end,
                    event.standard_timezone
                  ).join(" - ")}
                  pattern="headingFour"
                />
                <Element
                  id="subheading"
                  is={Text}
                  text={DEFAULT_SUBHEADING_TEXT}
                  pattern="paragraph"
                />
              </>
            )}
            <ButtonWrapper>
              <Element
                id="cta-button"
                is={Button}
                text={DEFAULT_BUTTON_TEXT}
                action={{
                  kind: Actions.TicketSelector,
                }}
              />
            </ButtonWrapper>
          </Content>
          <ImageWrapper>
            <Semicircle />
            <Semicircle />
            <QuarterCircle />
            <Circle />
            <Image
              imageFit={props.imageFit}
              $position={props.backgroundPosition || "center center"}
              src={props.backgroundImage}
              alt={props.backgroundImageAlt || ""}
            />
          </ImageWrapper>
        </Wrapper>
      </BlockMaxWidthWrapper>
    </BlockBackgroundColorWrapper>
  );
}

HeaderOption1.craft = {
  custom: {
    displayName: "Header option #1",
  },
  related: {
    settings: CustomHeaderSettings,
    leftOutlineOptions: TopLevelFloatingOptions,
  },
};
